"use client";

import { useMemo } from "react";
import { useCitationContext } from "./citation-context";
import { highlightText } from "@/features/arbitrator/lib/highlight";

interface HighlightedTextProps {
  children: string;
  className?: string;
  as?: "p" | "span" | "li";
  /** Restrict highlights to citations from this evidence source (e.g. "summary", "experience", "skills"). */
  source?: string;
}

export function HighlightedText({
  children: text,
  className,
  as: Tag = "span",
  source,
}: HighlightedTextProps) {
  const ctx = useCitationContext();
  const allCitations = ctx?.citations ?? [];
  const citations = source
    ? allCitations.filter((c) => c.source === source)
    : allCitations;

  const segments = useMemo(
    () => highlightText(text, citations),
    [text, citations],
  );

  const hasHighlights = segments.some((s) => s.citation !== null);

  if (!hasHighlights) {
    return <Tag className={className}>{text}</Tag>;
  }

  return (
    <Tag className={className}>
      {segments.map((seg, i) => {
        if (!seg.citation) {
          return <span key={i}>{seg.text}</span>;
        }
        const { color, number, source } = seg.citation;
        return (
          <mark
            key={i}
            data-citation={number}
            data-citation-source={source}
            style={{
              backgroundColor: `${color}18`,
              borderBottom: `1.5px solid ${color}`,
              color: "inherit",
              borderRadius: 0,
            }}
          >
            {seg.text}
            <sup
              className="ml-px select-none font-semibold"
              style={{ color, fontSize: "0.65em", lineHeight: 1 }}
            >
              [{number}]
            </sup>
          </mark>
        );
      })}
    </Tag>
  );
}
